import { inject, Injectable, signal } from '@angular/core';
import { lastValueFrom, Observable, tap } from 'rxjs';
import { MessageRestService, Partner, PartnerRestService, UpdatePartner } from '../../_generated';
import { Language } from '../models/language.models';
import { FileUtils } from '../utils/file.utils';
import { PartnerUtils } from '../utils/partner.utils';

@Injectable({
    providedIn: 'root',
})
export class PartnerService {
    private readonly partnerRestService = inject(PartnerRestService);
    private readonly messageService = inject(MessageRestService);

    // Create a signal to store partner data
    private partnerSignal = signal<Partner | null>(null);

    // Public readonly accessor for the partner signal
    readonly partner = this.partnerSignal.asReadonly();

    getLoggedInPartner(): Observable<Partner> {
        return this.partnerRestService.partnerControllerGetMe().pipe(tap((partner) => this.partnerSignal.set(partner)));
    }

    async updateName(firstName: string, lastName: string, policyNumber: string, files: File[]): Promise<void> {
        const fileContents = await Promise.all(files.map((file) => FileUtils.fileToFileContent(file)));
        const updateNamePayload = PartnerUtils.generateUpdateNamePayload(firstName, lastName, policyNumber, fileContents);

        await lastValueFrom(this.messageService.messageControllerSendMessage([updateNamePayload]));
    }

    // use message service because some stuff can't be updated directly. it must be done via the message service
    async updateCivilStatus(policyNumber: string, civilStatus: string): Promise<void> {
        const updateCivilStatusPayload = PartnerUtils.generateUpdateCivilStatusPayload(policyNumber, civilStatus);

        await lastValueFrom(this.messageService.messageControllerSendMessage([updateCivilStatusPayload]));
    }

    async updateAcquistionType(acquistionType: string): Promise<void> {
        const updateAcquistionTypePayload = PartnerUtils.generateUpdatePartnerPayload(this.partner(), {
            erwerbsart: acquistionType.toString(),
        });

        const patchPayload: UpdatePartner = {
            erwerbsart: acquistionType.toString(),
        };

        await lastValueFrom(this.partnerRestService.partnerControllerPatchMe(patchPayload));

        if (updateAcquistionTypePayload) {
            await lastValueFrom(this.messageService.messageControllerSendMessage([updateAcquistionTypePayload]));
        }
    }

    async updateAddress(
        adresseGueltigab: string,
        adresseLand: string,
        adressePostleitzahl: string,
        adresseOrt: string,
        adresseStrasse: string,
        adresseHausnummer: string,
    ): Promise<void> {
        const updateAddressPayload = PartnerUtils.generateUpdatePartnerPayload(this.partner(), {
            adresseGueltigab,
            adresseLand,
            adressePostleitzahl,
            adresseOrt,
            adresseStrasse,
            adresseHausnummer,
        });

        const patchPayload: UpdatePartner = {
            adresseGueltigab: adresseGueltigab,
            adresseLand: 'CH',
            adressePostleitzahl: adressePostleitzahl,
            adresseOrt: adresseOrt,
            adresseStrasse: adresseStrasse,
            adresseHausnummer: adresseHausnummer,
            adresseCo: '',
            adressePostfach: '',
            adresseZusatz: '',
            erwerbsart: this.partner()?.erwerbsart,
        };

        await lastValueFrom(this.partnerRestService.partnerControllerPatchMe(patchPayload));

        if (updateAddressPayload) await lastValueFrom(this.messageService.messageControllerSendMessage([updateAddressPayload]));
    }

    async updateContact(email: string, phone: string): Promise<void> {
        const updateContactPayload = PartnerUtils.generateUpdatePartnerPayload(this.partner(), {
            email,
            telefon: phone,
        });

        const patchPayload: UpdatePartner = {
            adresseCo: this.partner()?.adresseCo,
            adresseGueltigab: this.partner()?.adresseGueltigab,
            adresseHausnummer: this.partner()?.adresseHausnummer,
            adresseOrt: this.partner()?.adresseOrt,
            adressePostfach: this.partner()?.adressePostfach,
            adressePostleitzahl: this.partner()?.adressePostleitzahl,
            adresseStrasse: this.partner()?.adresseStrasse,
            adresseZusatz: this.partner()?.adresseZusatz,
            erwerbsart: this.partner()?.erwerbsart,
            email: email,
            telefon: phone,
        };

        await lastValueFrom(this.partnerRestService.partnerControllerPatchMe(patchPayload));

        if (updateContactPayload) await lastValueFrom(this.messageService.messageControllerSendMessage([updateContactPayload]));
    }

    async deactivatePortal(): Promise<void> {
        await lastValueFrom(
            this.partnerRestService.partnerControllerPatchMe({
                portalActive: false,
            } as UpdatePartner),
        );
    }

    async updateSmsNotificationsSetting(smsNotification: boolean): Promise<void> {
        await lastValueFrom(
            this.partnerRestService.partnerControllerPatchMe({
                benachrichtigungenSms: smsNotification,
            } as UpdatePartner),
        );
    }

    async updateEmailNotificationsSetting(emailNotification: boolean): Promise<void> {
        await lastValueFrom(
            this.partnerRestService.partnerControllerPatchMe({
                benachrichtigungenEmail: emailNotification,
            } as UpdatePartner),
        );
    }

    async updateFirstLogin() {
        await lastValueFrom(
            this.partnerRestService.partnerControllerPatchMe({
                firstLogin: false,
            } as UpdatePartner),
        );
        this.partnerSignal.set({ ...this.partnerSignal()!, firstLogin: false });
    }

    updatePartnerPortalLanguage(portalSprache: Language): Observable<Partner> {
        return this.partnerRestService
            .partnerControllerPatchMe({ portalSprache: portalSprache as never })
            .pipe(tap((updatedPartner) => this.partnerSignal.set(updatedPartner)));
    }
}
